import type { UseProctoringReturn } from 'proctoring-sdk/types'
import {
    DISCONNECT_REASON_MESSAGES,
    INVALID_SCREEN_SHARE_PREFIX,
    MULTIPLE_MONITORS_PREFIX,
    PROCTORING_ERROR_MESSAGES,
} from 'Components/Proctoring/proctoringPermissions.constants'
import {
    MediaTrackKind,
    PermissionKind,
    PermissionStatus,
    ProctoringDisconnectReason,
    ProctoringSessionStatus,
} from 'Components/Proctoring/proctoring.types'

export const isTerminalProctoringSessionStatus = (status?: string | null) => {
    if (!status) return false
    const normalized = String(status).trim().toLowerCase()
    return Object.values(ProctoringSessionStatus).some(value => value === normalized)
}

export const messageForDisconnectReason = (reason?: string | null) => {
    if (!reason) return null
    return (
        DISCONNECT_REASON_MESSAGES[reason] ||
        DISCONNECT_REASON_MESSAGES[ProctoringDisconnectReason.ScreenShareStopped]
    )
}

export const messageForPermissionError = (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error)
    if (message.startsWith(INVALID_SCREEN_SHARE_PREFIX) || message.includes('Entire Screen')) {
        return PROCTORING_ERROR_MESSAGES.SCREEN_SHARE_INVALID
    }
    if (message.startsWith(MULTIPLE_MONITORS_PREFIX)) {
        return PROCTORING_ERROR_MESSAGES.MULTIPLE_MONITORS
    }
    return PROCTORING_ERROR_MESSAGES.PERMISSION
}

export const hasLiveTrack = (stream: MediaStream | null | undefined, kind: MediaTrackKind) => {
    const tracks =
        kind === MediaTrackKind.Video ? stream?.getVideoTracks?.() : stream?.getAudioTracks?.()
    return !!tracks?.some(t => t.readyState === 'live')
}

export const statusFromPermissionValue = (value?: string): PermissionStatus => {
    if ([PermissionStatus.Denied, PermissionStatus.Granted].includes(value as PermissionStatus)) {
        return value as PermissionStatus
    }
    return PermissionStatus.Prompt
}

export const statusFor = (
    proctoring: UseProctoringReturn,
    permission: PermissionKind
): PermissionStatus => {
    const sdkState = proctoring.state?.sdkState || {}
    const { webcamStream, screenStream } = sdkState
    const permissions = proctoring.permissions || {}

    if (permission === PermissionKind.Webcam) {
        if (hasLiveTrack(webcamStream, MediaTrackKind.Video)) return PermissionStatus.Granted
        return statusFromPermissionValue(permissions.camera)
    }

    if (permission === PermissionKind.Microphone) {
        if (hasLiveTrack(webcamStream, MediaTrackKind.Audio)) return PermissionStatus.Granted
        return statusFromPermissionValue(permissions.microphone)
    }

    if (hasLiveTrack(screenStream, MediaTrackKind.Video)) return PermissionStatus.Granted
    return PermissionStatus.Prompt
}

export const trackDeviceId = (stream: MediaStream | null, kind: MediaTrackKind) => {
    if (!stream) return null
    const track =
        kind === MediaTrackKind.Video ? stream.getVideoTracks()?.[0] : stream.getAudioTracks()?.[0]
    return track?.getSettings?.()?.deviceId || null
}

export const replaceMediaTrack = async (
    stream: MediaStream | null,
    kind: MediaTrackKind,
    deviceId: string | null
) => {
    if (!stream || !deviceId) return
    const constraints =
        kind === MediaTrackKind.Video
            ? { video: { deviceId: { exact: deviceId } }, audio: false }
            : { audio: { deviceId: { exact: deviceId } }, video: false }

    const fresh = await navigator.mediaDevices.getUserMedia(constraints)
    const newTrack =
        kind === MediaTrackKind.Video ? fresh.getVideoTracks()[0] : fresh.getAudioTracks()[0]
    const oldTrack =
        kind === MediaTrackKind.Video ? stream.getVideoTracks()[0] : stream.getAudioTracks()[0]

    if (oldTrack && newTrack) {
        stream.removeTrack(oldTrack)
        stream.addTrack(newTrack)
        oldTrack.stop()
    }

    fresh.getTracks().forEach(track => {
        if (track !== newTrack) track.stop()
    })
}
